import { Contexts } from '@layers/web/constants/contexts.ts'
import type { AuthFeedback } from '@layers/web/stores/types/user.ts'
import type { AuthError, AuthErrorFeedbackContext, TranslateAuthFeedback } from '@layers/web/utils/auth/types/feedback.ts'

const AuthFeedbackMethods = {
  verify: 'VERIFY',
  login: 'LOGIN',
  signup: 'SIGNUP',
  resetPassword: 'RESET_PASSWORD',
}

const NETWORK_ERROR_CODE = 'NetworkError'

/**
 * Messages are translations, which the i18n module types loosely, hence the any.
 */
const createAuthFeedback = (
  message: any,
  code = 'unknown',
  type: string = Contexts.INFO,
  action: string | null = null,
  actionText: any = null
): AuthFeedback => ({
  code,
  message,
  action,
  actionText,
  type,
})

const withUsername = (method: string, username?: string): string => (
  username ? `${method}:${username}` : method
)

/**
 * Amplify reports a failed Cognito request in two shapes: amazon-cognito-identity-js sets code
 * 'NetworkError' on its Error('Network error'), while the fetch handler in @aws-amplify/core (the
 * federated credentials path) throws the same Error('Network error') without a code.
 */
const resolveAuthErrorCode = (error: AuthError): string => {
  if (error?.code) {
    return error.code
  }

  return error?.message === 'Network error' ? NETWORK_ERROR_CODE : ''
}

const createAuthFeedbackForKnownErrorCode = (
  code: string,
  translate: TranslateAuthFeedback,
  context: AuthErrorFeedbackContext = {}
): AuthFeedback | null => {
  switch (code) {
    case 'UsernameExistsException': {
      return createAuthFeedback(
        translate('memberUsernameExistsException'),
        code,
        Contexts.INFO,
        AuthFeedbackMethods.login,
        translate('memberFeedbackClickHereToLogIn')
      )
    }
    case 'UserNotConfirmedException': {
      return createAuthFeedback(
        translate('memberUserNotConfirmedException'),
        code,
        Contexts.INFO,
        withUsername(AuthFeedbackMethods.verify, context.username),
        translate('formResendCode')
      )
    }
    case 'CodeMismatchException':
    case 'ExpiredCodeException': {
      const method = context.context === 'passwordReset'
        ? AuthFeedbackMethods.resetPassword
        : AuthFeedbackMethods.verify

      return createAuthFeedback(
        translate('memberExpiredCodeException'),
        code,
        Contexts.ERROR,
        withUsername(method, context.username),
        translate('formResendCode')
      )
    }
    case 'NotAuthorizedException':
    case 'UserNotFoundException':
    case 'InvalidParameterException':
    case 'LimitExceededException':
    case NETWORK_ERROR_CODE: {
      return createAuthFeedback(translate(`member${code}`), code, Contexts.ERROR)
    }
    default: {
      return null
    }
  }
}

export {
  AuthFeedbackMethods,
  createAuthFeedback,
  createAuthFeedbackForKnownErrorCode,
  resolveAuthErrorCode,
}
